<template>
  <v-container fluid class="px-8">
    <div v-if="showAlert">
      <v-alert
        v-model="showAlert"
        border="left"
        close-text="Close Alert"
        dismissible
        :type="alertType"
        style="position: absolute;"
      >
        {{ alertMsg }}
      </v-alert>
    </div>

    <v-row class="ma-0 pa-0" justify="space-around">
      <v-col cols="12">
        <v-row class="ma-0 pa-0" align="center">
          <v-col cols="auto" class="ma-0 pa-0">
            <span class="page-title">Executive Overview</span>
          </v-col>
          <v-spacer></v-spacer>
          <v-col v-show="$vuetify.breakpoint.mdAndUp" md="auto" class="ma-0 pa-0 px-3">
            <DateRange
              :start_date="start_date"
              :end_date="end_date"
              :min_start_date="min_start_date"
              :max_end_date="max_end_date"
              v-on:date_update="update_date_range"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Main Statistics -->
    <v-row class="ma-0 pa-0" justify="space-around" v-if="charts_config && userPreferences">
      <v-col cols="4" class="ma-0 pa-3">
        <BaseChart
          chart_type="MultiGauge"
          :value="ticketing_overview_score"
          statistic_title_name="Ticketing Overview"
        >
        </BaseChart>
      </v-col>
      <v-col cols="4" class="ma-0 pa-3">
        <BaseChart
          chart_type="Gauge"
          :value="cyber_security_score['value']"
          statistic_title_name="Cyber Security Score"
        >
        </BaseChart>
      </v-col>
      <v-col cols="4" class="ma-0 pa-3">
        <BaseChart
          chart_type="Gauge"
          :value="cyber_insurance_score['value']"
          statistic_title_name="Cyber Insurance Score"
        >
        </BaseChart>
      </v-col>
    </v-row>
    <v-row class="ma-0 pa-0" justify="space-around" v-else>
      <v-col cols="4" style="height: 253.75px; overflow-y: hidden" class="ma-0 py-0">
        <v-skeleton-loader type="image, image"></v-skeleton-loader>
      </v-col>
      <v-col cols="4" style="height: 253.75px; overflow-y: hidden" class="ma-0 py-0">
        <v-skeleton-loader type="image, image"></v-skeleton-loader>
      </v-col>
      <v-col cols="4" style="height: 253.75px; overflow-y: hidden" class="ma-0 py-0">
        <v-skeleton-loader type="image, image"></v-skeleton-loader>
      </v-col>
    </v-row>

    <!-- Secondary Statistics -->
    <v-row
      class="ma-0 pa-0"
      justify="space-around"
      v-if="charts_config && userPreferences"
    >
      <v-col
        cols="3"
        class="ma-0 pa-3"
        v-for="(stat, idx) in userPreferences['data_points_stat']"
        :key="idx"
      >
        <MainPageStat
          :statistic_title_name="charts_config['data'][stat]['stat_title']"
          :value="charts_config['data'][stat]['stat_value']"
          :change="charts_config['data'][stat]['stat_change']"
          :odd_box="true"
        ></MainPageStat>
      </v-col>
    </v-row>
    <v-row class="ma-0 pa-0" justify="space-around" v-else>
      <v-col cols="3" v-for="idx in [1, 2, 3, 4]" :key="idx">
        <MainPageStat statistic_title_name="" :value="null" :change="null" :odd_box="true"></MainPageStat>
      </v-col>
    </v-row>

    <v-row
      class="ma-0 pa-0"
      justify="space-around"
      v-if="charts_config && userPreferences"
    >
      <v-col cols="6">
        <BaseChart
          statistic_title_name="Open Tickets By Type"
          :link="charts_config['charts']['ScoreOverview']['chart_link']"
          :value="ticketing_daily_review"
          :chart_type="charts_config['charts']['ScoreOverview']['chart_type']"
        />
      </v-col>
      <v-col cols="6">
        <BaseChart
          statistic_title_name="Open Ticket Threat Level"
          :link="charts_config['charts']['ScoreOverview']['chart_link']"
          :value="ticketing_heatmap"
          chart_type="HeatMap"
        />
      </v-col>

      <v-col cols="12">
        <div class="bottom-dashboard-grid">
          <!-- Left column -->
          <div class="grid-item device-offline-slot">
            <BaseChart
              :statistic_title_name="charts_config['charts'][userPreferences['graphs_stat'][0]]['chart_title']"
              :link="charts_config['charts'][userPreferences['graphs_stat'][0]]['chart_link']"
              :value="charts_config['charts'][userPreferences['graphs_stat'][0]]['chart_value']"
              :chart_type="charts_config['charts'][userPreferences['graphs_stat'][0]]['chart_type']"
            />
          </div>

          <div class="grid-item patch-status-slot">
            <BaseChart
              :statistic_title_name="charts_config['charts'][userPreferences['graphs_stat'][1]]['chart_title']"
              :link="charts_config['charts'][userPreferences['graphs_stat'][1]]['chart_link']"
              :value="charts_config['charts'][userPreferences['graphs_stat'][1]]['chart_value']"
              :chart_type="charts_config['charts'][userPreferences['graphs_stat'][1]]['chart_type']"
            />
          </div>

          <!-- Feed spans the two right-side chart positions -->
          <div class="grid-item otx-feed-slot">
            <HomeOTXFeed :refreshMs="60000" />
          </div>

          <!-- Moved below side by side -->
          <div>
            <BaseChart
              :statistic_title_name="charts_config['charts'][userPreferences['graphs_stat'][2]]['chart_title']"
              :link="charts_config['charts'][userPreferences['graphs_stat'][2]]['chart_link']"
              :value="charts_config['charts'][userPreferences['graphs_stat'][2]]['chart_value']"
              :chart_type="charts_config['charts'][userPreferences['graphs_stat'][2]]['chart_type']"
            />
          </div>

          <div>
            <BaseChart
              :statistic_title_name="charts_config['charts'][userPreferences['graphs_stat'][3]]['chart_title']"
              :link="charts_config['charts'][userPreferences['graphs_stat'][3]]['chart_link']"
              :value="charts_config['charts'][userPreferences['graphs_stat'][3]]['chart_value']"
              :chart_type="charts_config['charts'][userPreferences['graphs_stat'][3]]['chart_type']"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0" justify="space-around" v-else>
      <v-col cols="6" style="height: 288px; overflow-y: hidden">
        <v-skeleton-loader type="image, image"></v-skeleton-loader>
      </v-col>
      <v-col cols="6" style="height: 288px; overflow-y: hidden">
        <v-skeleton-loader type="image, image"></v-skeleton-loader>
      </v-col>
      <v-col cols="12">
        <div class="bottom-dashboard-grid">
          <div class="grid-item device-offline-slot" style="height: 288px; overflow-y: hidden">
            <v-skeleton-loader type="image, image"></v-skeleton-loader>
          </div>
          <div class="grid-item patch-status-slot" style="height: 288px; overflow-y: hidden">
            <v-skeleton-loader type="image, image"></v-skeleton-loader>
          </div>
          <div class="grid-item otx-feed-slot" style="height: 596px; overflow-y: hidden">
            <v-skeleton-loader type="image, image, image, image"></v-skeleton-loader>
          </div>
          <div class="grid-item devices-by-type-slot" style="height: 288px; overflow-y: hidden">
            <v-skeleton-loader type="image, image"></v-skeleton-loader>
          </div>
          <div class="grid-item antivirus-status-slot" style="height: 288px; overflow-y: hidden">
            <v-skeleton-loader type="image, image"></v-skeleton-loader>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import MainPageStat from "./StatisticComponents/MainPageStat";
import BaseChart from "./StatisticComponents/Charts/BaseChart.vue";
import DateRange from "./UtilityComponents/DateRange.vue";
import api_caller from "@/javascript/chart_config_retrieval.js";
import { ALERT_GENERIC_MESSAGE, ALERT_TYPE_ERROR } from "@/constants/constants.js";
import HomeOTXFeed from "./Sections/HomeOTXFeed.vue";

export default {
  name: "BasePage",
  props: [
    "start_date",
    "end_date",
    "system_min_start_date",
    "system_max_end_date"
  ],
  data: () => ({
    home_page_raw: null,
    cyber_security_score: {
      title: "QuickProtect Score",
      value: null,
      change: null,
    },
    cyber_insurance_score: {
      title: "Cyber Insurance Score",
      value: null,
    },
    ticketing_overview_score: [
      ["Created", 0],
      ["Open", 0],
      ["Closed", 0]
    ],
    ticketing_heatmap: null,
    ticketing_daily_review: null,
    charts_config: null,
    alertMsg: null,
    alertType: null,
    showAlert: false,
    userPreferences: null,
    quick_protect_score: null,
    date_range: [],
    min_start_date: "2024-01-01",
    max_end_date: "2024-12-31",
  }),
  components: {
    MainPageStat,
    BaseChart,
    DateRange,
    HomeOTXFeed
  },

  mounted() {
    this.date_range = [this.start_date, this.end_date];
    this.min_start_date = this.system_min_start_date;
    this.max_end_date = this.system_max_end_date;
    this.getUserPreferences();
    this.getChartConfig();
  },

  methods: {
    load_stat_value: function (dataset, stat_path) {
      let tmp = dataset;
      for (let path_val of stat_path) {
        tmp = tmp[path_val];
      }
      return tmp;
    },

    update_date_range: function(date_range){
      this.date_range = date_range;
      this.$emit("system_date_update", { start: date_range[0], end: date_range[1] });
      this.getChartConfig();
    },

    getChartConfig: async function () {
      this.charts_config = null;
      this.cyber_insurance_score = null;
      this.cyber_security_score = null;

      const token = await this.$auth.getTokenSilently();
      let charts_config = await api_caller
        .get_home_charts_config(token, this.date_range[0], this.date_range[1])
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          } else {
            return false;
          }
        })
        .catch((e) => {
          console.log(e);
          return false;
        });

      if (charts_config) {
        this.charts_config = charts_config;

        this.cyber_security_score = this.charts_config["ScoreOverview"]["cyber_security_score"];
        this.cyber_insurance_score = this.charts_config["ScoreOverview"]["cyber_insurance_score"];
        this.ticketing_overview_score = charts_config["charts"]["TicketsOverview"]["chart_value"];
        this.ticketing_daily_review = charts_config["charts"]["TicketsStatusDailyByType"]["chart_value"];
        this.ticketing_heatmap = charts_config["charts"]["TicketsHeatMap"]["chart_value"];
      } else {
        this.triggerAlert(ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }
    },
    getUserPreferences: async function () {
      let token = await this.$auth.getTokenSilently();
      let userPreferences = await api_caller
        .get_homepage_user_preferences(token)
        .then((resp) => {
          return resp["data"];
        })
        .catch((e) => {
          console.log(e);
          return false;
        });
      if (userPreferences) {
        this.userPreferences = userPreferences;
      }
    },

    getChartByTitle: function (title) {
      if (!this.charts_config || !this.charts_config["charts"]) {
        return null;
      }

      const charts = this.charts_config["charts"];
      const preferredGraphKeys =
        this.userPreferences && this.userPreferences["graphs_stat"]
          ? this.userPreferences["graphs_stat"]
          : [];

      for (let i = 0; i < preferredGraphKeys.length; i++) {
        const graphKey = preferredGraphKeys[i];
        if (
          charts[graphKey] &&
          charts[graphKey]["chart_title"] === title
        ) {
          return charts[graphKey];
        }
      }

      const allKeys = Object.keys(charts);
      for (let i = 0; i < allKeys.length; i++) {
        const key = allKeys[i];
        if (charts[key] && charts[key]["chart_title"] === title) {
          return charts[key];
        }
      }

      return null;
    },

    triggerAlert: function (type, message) {
      this.alertType = type ? type : "info";
      this.alertMsg = message;
      this.showAlert = true;
    },
  },
};
</script>

<style scoped>
.tab_title {
  font-family: "Gothic Bold";
  font-size: 0.8em;
  color: black;
}

.last_updated_title{
  font-family: "Gothic Bold" !important;
  font-size: 0.8em !important;
}

.last_updated_text{
  font-family: "Gothic" !important;
  font-size: 0.85em !important;
}

.bottom-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    "deviceOffline otxFeed"
    "patchStatus otxFeed"
    "devicesByType antivirusStatus";
  gap: 24px;
  width: 100%;
}

.grid-item {
  min-width: 0;
}

.device-offline-slot {
  grid-area: deviceOffline;
}

.patch-status-slot {
  grid-area: patchStatus;
}

.otx-feed-slot {
  grid-area: otxFeed;
}

.devices-by-type-slot {
  grid-area: devicesByType;
}

.antivirus-status-slot {
  grid-area: antivirusStatus;
}

@media (max-width: 1263px) {
  .bottom-dashboard-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      "otxFeed"
      "deviceOffline"
      "patchStatus"
      "devicesByType"
      "antivirusStatus";
  }
}
</style>